'use client';

import { cn } from '@reelplicate/ui/lib/utils';
import { Progress as ProgressPrimitive } from 'radix-ui';
import type * as React from 'react';

/**
 * Radix Progress, restyled to the Figma Progress Bar: a bg/emphasis track that
 * clips a brand/strong Fill. The fill is sized by `width` (Figma: "Animate
 * width 300ms ease-out"), not shadcn's translateX, so the Fill box is the value.
 * `value={null}` is indeterminate (Radix omits `aria-valuenow`); the caller
 * styles that runner through `indicatorClassName`.
 */
function Progress({
  className,
  indicatorClassName,
  value,
  max = 100,
  ...props
}: React.ComponentProps<typeof ProgressPrimitive.Root> & {
  indicatorClassName?: string;
}) {
  // Radix treats an out-of-range value as invalid (indeterminate): clamp it.
  const clamped = value == null ? null : Math.min(max, Math.max(0, value));
  return (
    <ProgressPrimitive.Root
      data-slot="progress"
      value={clamped}
      max={max}
      className={cn(
        'bg-bg-emphasis relative block h-2 w-full overflow-hidden rounded-xs',
        className,
      )}
      {...props}
    >
      <ProgressPrimitive.Indicator
        data-slot="progress-indicator"
        className={cn(
          'bg-brand-strong block h-full rounded-[inherit]',
          'transition-[width] duration-300 ease-out motion-reduce:transition-none',
          indicatorClassName,
        )}
        style={clamped == null ? undefined : { width: `${(clamped / max) * 100}%` }}
      />
    </ProgressPrimitive.Root>
  );
}

export { Progress };
